import { Link, List, ListItem, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

type ListLevel = 1 | 2;

const listStyleByLevel: Record<ListLevel, 'decimal' | 'lower-alpha'> = {
  1: 'decimal',
  2: 'lower-alpha',
};

const policyListSx = (level: ListLevel): SystemStyleObject<Theme> => ({
  listStyleType: listStyleByLevel[level],
  listStylePosition: 'outside',
  margin: 0,
  paddingLeft: 2.75,
  ...(level === 2 ? { marginTop: 0.75, marginBottom: 0 } : {}),
});

const policyListItemSx: SystemStyleObject<Theme> = {
  display: 'list-item',
  padding: 0,
  paddingLeft: 0.5,
  marginBottom: 1,
  '&:last-child': {
    marginBottom: 0,
  },
};

const policyUnorderedListSx: SystemStyleObject<Theme> = {
  listStyleType: 'square',
  listStylePosition: 'outside',
  margin: 0,
  marginTop: 0.75,
  marginBottom: 0,
  paddingLeft: 2.75,
};

const PolicyOrderedList = ({ level, children }: { level: ListLevel; children: ReactNode }) => (
  <List component="ol" disablePadding sx={policyListSx(level)}>
    {children}
  </List>
);

const PolicyUnorderedList = ({ children }: { children: ReactNode }) => (
  <List component="ul" disablePadding sx={policyUnorderedListSx}>
    {children}
  </List>
);

const PolicyListItem = ({ children }: { children: ReactNode }) => (
  <ListItem component="li" disableGutters sx={policyListItemSx}>
    {children}
  </ListItem>
);

const PolicyParagraph = ({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) => (
  <Typography variant="body1" component="p" sx={{ margin: 0, ...sx }}>
    {children}
  </Typography>
);

const PolicySectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography variant="body1" component="span" sx={{ fontWeight: 700 }}>
    {children}
  </Typography>
);

const ExternalLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" underline="always">
    {children}
  </Link>
);

const EmailLink = ({ address }: { address: string }) => (
  <Link href={`mailto:${address}`} underline="always">
    {address}
  </Link>
);

const BpsPrivacyPolicyContent = () => (
  <Stack spacing={2.5}>
    <PolicyParagraph>
      <PolicySectionTitle>Last Revised:</PolicySectionTitle> June 2, 2026
    </PolicyParagraph>

    <PolicyParagraph>
      Sphero, Inc. (&ldquo;Sphero&rdquo;, &ldquo;we&rdquo; or &ldquo;us&rdquo;) is committed to protecting your and, where applicable, your
      child&rsquo;s privacy. When you use Blueprint Studio, including accessing its website or using robots controlled by the application, we do not
      collect your personal data. We only collect personal data in connection with Blueprint Studio if you contact us for support.
    </PolicyParagraph>

    <PolicyParagraph>
      This Privacy Policy describes what data is collected, used, and disclosed by Sphero when you use Blueprint Studio (the
      &ldquo;Application&rdquo;), which is accessible at <ExternalLink href="https://bps.sphero.com">https://bps.sphero.com</ExternalLink> (the
      &ldquo;Site&rdquo;), to control robots (the &ldquo;Robot(s)&rdquo;). This policy only applies to the services we offer through our Site,
      Application, and Robots (collectively, the &ldquo;Services&rdquo;). For our privacy practices regarding data collected outside the Services,
      please consult our general privacy policy, which is accessible at:{' '}
      <ExternalLink href="https://sphero.com/pages/privacy">https://sphero.com/pages/privacy</ExternalLink>.
    </PolicyParagraph>

    <PolicyOrderedList level={1}>
      <PolicyListItem>
        <PolicyParagraph>
          <PolicySectionTitle>Data We Collect.</PolicySectionTitle> We collect the following types of data:
        </PolicyParagraph>
        <PolicyOrderedList level={2}>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Device Data.</PolicySectionTitle> This includes, for example, data regarding the user&rsquo;s computer device,
              operating system, and browser that the Application is running in.
            </PolicyParagraph>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Usage Data.</PolicySectionTitle> This includes, for example, data regarding Application page views, feature usage,
              and user interface interactions. This data is not tied to any personally identifiable data.
            </PolicyParagraph>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Project Data.</PolicySectionTitle> This includes, for example, programs and dashboard configuration data a user
              chooses to store on the Application servers. Such data is accessible by a randomized code and PIN.
            </PolicyParagraph>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Robot Data.</PolicySectionTitle> This includes, for example, a Robot identifier, date/time stamps when a Robot
              connects to an Application, non-personal diagnostic data, Robot hardware sensor data, crash logs, project code, and the like. This
              information is transmitted to the Application via a USB or Bluetooth connection.
            </PolicyParagraph>
          </PolicyListItem>
        </PolicyOrderedList>
        <PolicyParagraph sx={{ mt: 1.5 }}>
          The above data does not contain any personal data. But, if you contact us for support through the Application, we may collect the following
          personal data: first and last name, email address, and any information you choose to include in your query. We process that information
          based on our legitimate interest in responding to customer requests and providing customer service.
        </PolicyParagraph>
      </PolicyListItem>

      <PolicyListItem>
        <PolicyParagraph>
          <PolicySectionTitle>Use of Data.</PolicySectionTitle> If we collect personal data in connection with a support request, we only use that
          information to address your request. We use the other data we collect in different ways depending:
        </PolicyParagraph>
        <PolicyOrderedList level={2}>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Device Data.</PolicySectionTitle> We use the Device Data for our legitimate business interests in the improvement of
              our Services, including:
            </PolicyParagraph>
            <PolicyUnorderedList>
              <PolicyListItem>
                <PolicyParagraph>to understand engagement with our Services on different devices, operating systems, and browsers;</PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>
                  to improve the quality of experience when you interact with our Services on different devices, operating systems, and browsers; and
                </PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>to operate and improve our internal quality assurance and support operations.</PolicyParagraph>
              </PolicyListItem>
            </PolicyUnorderedList>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Usage Data.</PolicySectionTitle> We use the Usage Data for our legitimate business interests in the improvement of
              our Services and the creation of an optimized user experience, including:
            </PolicyParagraph>
            <PolicyUnorderedList>
              <PolicyListItem>
                <PolicyParagraph>
                  to understand engagement in an activity or feature, for example which features are used, time spent on pages, and data about any
                  crashes during use;
                </PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>to operate and improve our Services, internal operations, and systems;</PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>to improve the quality of experience when you interact with our Services;</PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>to understand your preferences to enhance your experience and enjoyment using our Services;</PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>to derive information about the demographics that use our Services; and</PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>to protect, investigate, and deter against fraudulent, unauthorized, or illegal activity.</PolicyParagraph>
              </PolicyListItem>
            </PolicyUnorderedList>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Project Data.</PolicySectionTitle> We use the Project Data for the creation of a personalized user experience,
              including:
            </PolicyParagraph>
            <PolicyUnorderedList>
              <PolicyListItem>
                <PolicyParagraph>
                  to store Projects on our platform for the purpose of secure storage and retrieval with a code and PIN; and
                </PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>
                  to support sharing of Projects with relevant parties (teachers, students, parents, etc.) with a share code.
                </PolicyParagraph>
              </PolicyListItem>
            </PolicyUnorderedList>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Robot Data.</PolicySectionTitle> We use the Robot Data for our legitimate business interests in the improvement of
              our Application and Robots, including in the following ways:
            </PolicyParagraph>
            <PolicyUnorderedList>
              <PolicyListItem>
                <PolicyParagraph>
                  to understand engagement with the Robot, for example which hardware features are used, what hardware components are connected to
                  Robot, and data about any Robot hardware or firmware issues during use; and
                </PolicyParagraph>
              </PolicyListItem>
              <PolicyListItem>
                <PolicyParagraph>to improve the quality of experience when you interact with our Application and Robot.</PolicyParagraph>
              </PolicyListItem>
            </PolicyUnorderedList>
          </PolicyListItem>
        </PolicyOrderedList>
      </PolicyListItem>

      <PolicyListItem>
        <PolicyParagraph>
          <PolicySectionTitle>Disclosures.</PolicySectionTitle> We do not disclose your personal data to our vendors or any other third parties. We
          use third-party vendors to help provide and host the Application and the Service; to help us automatically collect the Device Data, Usage
          Data, Project Data, and Robot Data; to analyze that data; and to further our other legitimate business interests. Our vendors only process
          that data for us, and they do so based on our instructions and in compliance with this Privacy Policy and any other appropriate
          confidentiality and security measures.
        </PolicyParagraph>
      </PolicyListItem>

      <PolicyListItem>
        <PolicyParagraph>
          <PolicySectionTitle>Rights and Choices.</PolicySectionTitle> If we collect your personal data, which we only do when you contact us for
          support, you may have the following rights regarding your information:
        </PolicyParagraph>
        <PolicyOrderedList level={2}>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Access.</PolicySectionTitle> You may request a list of your personal data that we process. At your request, we will
              send you a copy of the personal data we hold about you in a common, portable format of our choice.
            </PolicyParagraph>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Rectification.</PolicySectionTitle> You may request that we correct any personal data that we hold about you.
            </PolicyParagraph>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Objection to Processing.</PolicySectionTitle> You may request that we stop processing your personal data.
            </PolicyParagraph>
          </PolicyListItem>
          <PolicyListItem>
            <PolicyParagraph>
              <PolicySectionTitle>Erasure.</PolicySectionTitle> You may request that we delete your personal data from our systems.
            </PolicyParagraph>
          </PolicyListItem>
        </PolicyOrderedList>
        <PolicyParagraph sx={{ mt: 1.5 }}>
          You can exercise those rights by emailing Sphero&rsquo;s Data Protection Officer at <EmailLink address="dpo@sphero.com" /> with a copy to{' '}
          <EmailLink address="support@sphero.com" />. Alternatively, you can mail a letter with your request to:
        </PolicyParagraph>
        <Typography
          variant="body1"
          component="address"
          sx={{
            fontStyle: 'normal',
            whiteSpace: 'pre-line',
            mt: 1,
            mb: 0,
          }}
        >
          {`Sphero, Inc.
Attn: Legal, Privacy Policy
3216 Parkside Ct
Caddo Mills, TX 75135`}
        </Typography>
        <PolicyParagraph sx={{ mt: 1.5 }}>We will fulfill your request to the extent required by law.</PolicyParagraph>
        <PolicyParagraph sx={{ mt: 1.5 }}>
          You also have the right to contact or file a complaint with regulators or supervisory authorities about our processing of personal data. To
          do so, please contact your local data protection or consumer protection authority.
        </PolicyParagraph>
      </PolicyListItem>

      <PolicyListItem>
        <PolicyParagraph>
          <PolicySectionTitle>Changes to This Privacy Policy.</PolicySectionTitle> This Privacy Policy is subject to occasional revision. If we make
          any changes to this Policy, we will change the &ldquo;Last Revised&rdquo; date above.
        </PolicyParagraph>
      </PolicyListItem>

      <PolicyListItem>
        <PolicyParagraph>
          <PolicySectionTitle>Contact Us.</PolicySectionTitle> You can contact us with any questions by emailing Sphero&rsquo;s Data Protection
          Officer at <EmailLink address="dpo@sphero.com" /> or <EmailLink address="support@sphero.com" />.
        </PolicyParagraph>
      </PolicyListItem>
    </PolicyOrderedList>
  </Stack>
);

export default BpsPrivacyPolicyContent;
